import React, { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery } from "react-query";

// hooks
import { useHistory, useParams } from "react-router";
import { usePermissionsIndex } from "../../../../hooks/api/common/permissions";
import { useFacilityIndex } from "../../../../hooks/api/common/useFacilityIndex";
import {
  fallbackFulfilledFromMapping,
  FulfilledFromState,
  getFulfilledFrom,
} from "lib/fulfillment/fulfilledFromMapping";

// assets
import { SaveOutlined } from "@ant-design/icons";

// components
import { message, notification, Button, Spin, Row, Col } from "antd";
import ShipmentMethodSection from "./ShipmentMethodSection";
import { Organization as OrganizationLib } from "@secondcloset/fulfillment-utils";

import FulfillmentBackButton from "../../../fulfillment/FulfillmentBackButton";
import OrganizationDetailsForm from "./OrganizationDetailsForm";
import AssignPermissionsForm from "../../../../components/_common/organization/AssignPermissionsForm";
import UploadLogo from "../../../../components/UploadLogo";
import AddressForm from "./AddressForm";
import ShippingPreferencesSection from "./ShippingPreferencesSection";
import ParentOrganizationSection from "./ParentOrganizationSection";
import HubbleMerchantSection from "./HubbleMerchantSection";

// context
import { useOrganizationContext } from "contextProviders/organization/OrganizationProvider";

// lib
import { objectToFormData } from "object-to-formdata";
import { get, isEmpty, last } from "lodash-es";
import Organization from "../../../../lib/organization";
import Permissions from "../../../../lib/regular/permissions";
import GeofilterForm from "./GeofilterForm";
import { Helmet } from "react-helmet";
import { phone as phoneValidator } from "phone";
import iso from "iso-3166-1";
import countriesData from "api/common/data/countries.json";

// api
import { updateOrganizationFsaDetails, fetchOrganizationDetails } from "api/accounts/organization";

import { Common } from "@secondcloset/types";
import useUser from "hooks/reduxContainer/_common/useUser";
import { fetchUserRole } from "api/accounts/roles";
import { GeneralFulfillmentConfig } from "../OrganizationDetailsPage";
type RecommendedShippingMethod = Exclude<Common.Organization["recommended_shipping_methods"], undefined>;
type OrgShippingConfig = Common.OrganizationShippingConfig;
type ServiceTier = Exclude<RecommendedShippingMethod["sc_service_tier"], undefined>;
type GeofilterConstraint = Common.GeofilterConstraint;
type OrganizationType = Common.Organization;
type ExtendedOrganization = OrganizationType & {
  permission_ids: never[];
  addresses_attributes: {
    country: string;
    city: string;
    address: string;
    province: string;
    postal_code: string;
    apartment_number: string | null;
    building_type: string;
    elevator_access: boolean;
    is_primary: boolean;
  }[];
  logo_attributes: {
    file: File | string;
  };
  parent_organization_id: string | null;
};
type OrganizationConfig = Common.OrganizationConfig;
type SCServiceTier = Common.SCServiceTier;

const OrganizationCreateEditPage = () => {
  const facilityIndex = useFacilityIndex();

  const [uploadLogoLink, setUploadLogoLink] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLogoImageUpdated, setIsLogoImageUpdated] = useState(false);
  const [addressLine1, setAddressLine1] = useState("");
  const [unitNumber, setUnitNumber] = useState<string | null>("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [eim, setEim] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [packingNotes, setPackingNotes] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [whitelistCarriers, setWhitelistCarriers] = useState<Array<string>>([]);
  const [blacklistCarriers, setBlacklistCarriers] = useState<Array<string>>([]);
  const [fulfilledFrom, setFulfilledFrom] = useState<FulfilledFromState>(fallbackFulfilledFromMapping);

  const organizationDetails = useOrganizationContext();
  const [geofilterConstraints, setGeofilterConstraints] = useState<GeofilterConstraint[]>([]);

  const [fulfillmentConfigs, setFulfillmentConfigs] = useState(
    (organizationDetails.data?.configurations_attributes.reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {}) ||
      {}) as Record<Common.OrganizationConfigKey, unknown>
  );

  // default all enabled and selected
  const [enabledShippingMethod, setEnabledShippingMethod] = useState({
    parcel: true,
    freight: true,
    sc_last_mile: true,
  });
  const [selectedSCServiceTier, setSelectedSCServiceTier] = useState<ServiceTier>({
    threshold: true,
    room_of_choice: true,
    white_glove: true,
  });

  const [allowPartialFulfilledFrom, setAllowPartialFulfilledFrom] = useState(
    organizationDetails.data?.configurations_attributes.find(
      (c) => c.key === "use_default_facility_for_partial_fallback"
    )?.value === "true"
  );

  // Parent organization state
  const [parentOrganization, setParentOrganization] = useState<{ id: string | null; name: string }>({
    id: null,
    name: "",
  });
  const [selectedParentOrg, setSelectedParentOrg] = useState<{ label: string; value: string } | undefined>(undefined);

  const handleParentOrganizationChange = (orgId: string | null, orgName: string) => {
    setParentOrganization({ id: orgId, name: orgName });
    setSelectedParentOrg(orgId ? { label: orgName, value: orgId } : undefined);
  };

  const { mutateAsync: doUpdateOrganizationFSADetails } = useMutation(
    (parameters: { body: unknown; org_id: string }) => updateOrganizationFsaDetails(parameters.org_id, parameters.body),
    {}
  );

  const { fetchPermissionsIndex, permissionsIndex } = usePermissionsIndex();
  const history = useHistory();
  const params = useParams();
  const organizationID = get(params, "organization_id", "");
  const isGoBolt = organizationDetails.data && Organization.isGoBolt(organizationDetails.data.name);

  const {
    user: { organization_role_id: orgRoleID },
  } = useUser();

  const role = useQuery(["fetchUserRole", orgRoleID], () => fetchUserRole(orgRoleID), {
    enabled: !!orgRoleID,
  });

  // Fetch parent organization details when parentOrganizationId is provided
  useQuery(
    ["parentOrganization", parentOrganization.id],
    () => {
      if (!parentOrganization.id) {
        throw new Error("No parent organization ID provided");
      }
      return fetchOrganizationDetails(parentOrganization.id);
    },
    {
      enabled: !!parentOrganization.id,
      onSuccess: (data) => {
        setSelectedParentOrg({ label: data.name, value: data.id });
      },
    }
  );

  const isFacilityServiceAreaEditable = useMemo(() => {
    return !!role.data?.permissions.map((p) => p.name).includes("manage_facility_service_areas");
  }, [role.data]);

  const updateOrganizationFulfilledFrom = async (orgID: string) => {
    if (!isFacilityServiceAreaEditable)
      return notification.error({
        message: "Oops something went wrong.",
        description: "Please check if you have permission to update country default fulfillment fallback.",
      });

    Object.values(fulfilledFrom)
      .filter((f) => !!f.country_code && f.facility !== "unknown" && f.country_code !== "OTHER")
      .forEach(({ id, country_code, facility }) => {
        const facilityId = facilityIndex.data?.find((f) => f.name === facility)?.id;

        doUpdateOrganizationFSADetails({
          body: {
            facility_id: facilityId,
            facility_service_areas_attributes: [{ id, country_code }],
          },
          org_id: orgID,
        });
      });
  };

  const prefillForm = async () => {
    if (organizationDetails.data) {
      const organization: OrganizationType = organizationDetails.data;

      const name = get(organization, "name", "");
      const phone = get(organization, "phone_number", "");
      const email = get(organization, "email", "");
      const packingNotes = get(organization, "packing_notes", "");
      const uploadLogoLink = get(organization, "logo.url", "");

      setName(name);
      setPhone(phone);
      setEmail(email);
      setEim(organization?.employer_identification_number || "");
      setBusinessName(organization?.business_name || "");
      setPackingNotes(packingNotes);
      setUploadLogoLink(uploadLogoLink);
      setGeofilterConstraints(get(organization, "geofilter_constraints", []));

      const orgAddressList = get(organization, "addresses");
      const mostRecentOrgAddress = last(orgAddressList);
      const address = get(mostRecentOrgAddress, "address", "");
      const unitNumber = get(mostRecentOrgAddress, "apartment_number", "");
      const city = get(mostRecentOrgAddress, "city", "");
      const province = get(mostRecentOrgAddress, "province", "");
      const postalCode = get(mostRecentOrgAddress, "postal_code", "");
      const country = get(mostRecentOrgAddress, "country", "");

      setAddressLine1(address);
      setUnitNumber(unitNumber);
      setCity(city);
      setProvince(province);
      setPostalCode(postalCode);
      setCountry(country);

      // fill shipping configurations
      const shippingConfigs: OrgShippingConfig[] = get(organization, "shipping_configurations", []);
      const whitelisted = shippingConfigs.find((config) => config.key === "whitelist_carrier")?.value;
      if (whitelisted) setWhitelistCarriers(whitelisted);
      const blacklisted = shippingConfigs.find((config) => config.key === "blacklist_carrier")?.value;
      if (blacklisted) setBlacklistCarriers(blacklisted);

      // fill permissions form
      const organizationPermissions = get(organization, "permissions", []);
      const selectedPermissions = Permissions.getPermissionIdsArray(organizationPermissions);
      setSelectedPermissions(selectedPermissions);

      // fill shipment method
      const shippingMethod: Partial<OrganizationType["recommended_shipping_methods"]> =
        organization?.recommended_shipping_methods ?? {};
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { parcel = false, freight = false, sc_last_mile = false, sc_service_tier } = shippingMethod;

      setEnabledShippingMethod({ parcel, freight, sc_last_mile });
      setSelectedSCServiceTier(sc_service_tier as SCServiceTier);

      // fill fulfilled from

      if (organizationDetails.data.default_fulfilled_from) {
        const fulfilledFrom = getFulfilledFrom(organizationDetails.data.default_fulfilled_from);
        fulfilledFrom && setFulfilledFrom(fulfilledFrom);
      }

      // fill parent organization
      const parentOrgId = get(organization, "parent_organization_id", null);
      const parentOrgName = get(organization, "parent_organization_name", "");
      setParentOrganization({ id: parentOrgId, name: parentOrgName });
      setSelectedParentOrg(parentOrgId ? { label: parentOrgName, value: parentOrgId } : undefined);
    }
  };

  // eslint-disable-next-line
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchPermissionsIndex();
    prefillForm();
  }, [organizationDetails.data]);

  useEffect(() => {
    const error = organizationDetails.error || permissionsIndex.error;
    if (error) {
      notification.error({
        message: "Error",
        description: error,
      });
    }
  }, [organizationDetails.error || permissionsIndex.error]);

  if (!permissionsIndex.data) return null;

  const lookupCountry = (countryInput: string): string | undefined => {
    const normalizedCountry = countryInput.trim();
    if (!normalizedCountry) return undefined;
    const byIso =
      iso.whereCountry(normalizedCountry) || iso.whereAlpha2(normalizedCountry) || iso.whereAlpha3(normalizedCountry);
    if (byIso) return byIso.alpha2;

    return countriesData.find((c) => c.name.common.toLowerCase() === normalizedCountry.toLowerCase())?.cca3;
  };

  const isVerified = (segment: string) => {
    switch (segment) {
      case name:
        return name.length >= 2;
      case email:
        return /\S+@\S+\.\S+/.test(email);
      case phone: {
        const countryCode = lookupCountry(country);
        const isTollFree = /^\+?1?(800|833|844|855|866|877|888|822)/.test(phone);
        return !!phone && phoneValidator(phone, isTollFree ? {} : { country: countryCode }).isValid;
      }
      default:
        return false;
    }
  };

  interface RequiredFields {
    "company logo"?: string | File | null;
    country?: string;
    city?: string;
    province?: string;
    "address line 1"?: string;
    "postal code"?: string;
    imageFile?: File;
    uploadLogoLink?: string;
  }

  const isFormValid = () => {
    const isNameValid = isVerified(name);
    const isPhoneValid = isVerified(phone);
    const isEmailValid = isVerified(email);
    if (!isNameValid) message.error("Name must be at least 2 characters.");
    if (!isPhoneValid) message.error("Please enter a valid phone number.");
    if (!isEmailValid) message.error("Please make sure email is correct.");
    if (!isNameValid || !isPhoneValid || !isEmailValid) return false;
    let requiredFields: RequiredFields[] = [{ "company logo": uploadLogoLink || imageFile }];
    if (addressLine1 || country || city || postalCode || province) {
      requiredFields = [
        ...requiredFields,
        { country: country },
        { city: city },
        { "postal code": postalCode },
        { province: province },
        { "address line 1": addressLine1 },
      ];
    }
    const overlappingCarriers = whitelistCarriers.filter((c) => blacklistCarriers.includes(c));
    if (overlappingCarriers.length) {
      message.error(`${overlappingCarriers.join(", ")} cannot be a preferred carrier and an excluded carrier`);
      return false;
    }
    const missingFields = requiredFields.filter((requiredField) => {
      const key = Object.keys(requiredField)[0];
      return !requiredField[key as keyof typeof requiredField] ? key : false;
    });
    if (missingFields.length) {
      message.error(
        `The following field${missingFields.length > 1 ? "s are" : " is"} required: ${missingFields
          .map((missingField, index) =>
            missingFields.length > 1 && index + 1 === missingFields.length
              ? `and ${Object.keys(missingField)[0]}`
              : Object.keys(missingField)[0]
          )
          .join(", ")}.`
      );
      return false;
    }
    return true;
  };

  const onFormSubmit = async () => {
    if (!isFormValid()) return;

    const configurationsAttributes = [
      ...Object.entries(fulfillmentConfigs).map(([key, value]) => ({ key, value })),
      {
        key: "default_fulfilled_from",
        value: fulfilledFrom["OTHER"]?.facility ?? OrganizationLib.DEFAULT_FALLBACK_FACILITY_CODE,
      },
      {
        key: "use_default_facility_for_partial_fallback",
        value: String(allowPartialFulfilledFrom),
      },
    ] as OrganizationConfig[];

    const shippingConfigs: OrgShippingConfig[] = [
      {
        key: "whitelist_carrier",
        value: whitelistCarriers as string[],
      },
      {
        key: "blacklist_carrier",
        value: blacklistCarriers as string[],
      },
    ];

    const body: { organization: Partial<ExtendedOrganization> } = {
      organization: {
        name,
        email,
        phone_number: phoneValidator(phone, { country: lookupCountry(country) }).phoneNumber || phone,
        packing_notes: packingNotes,
        permission_ids: selectedPermissions,
        recommended_shipping_methods: {
          ...enabledShippingMethod,
          sc_service_tier: selectedSCServiceTier,
        },
        geofilter_constraints: geofilterConstraints,
        configurations_attributes: configurationsAttributes,
        shipping_configurations_attributes: shippingConfigs,
        employer_identification_number: eim,
        business_name: businessName,
        parent_organization_id: parentOrganization.id,
      },
    };

    if (addressLine1) {
      const address = addressLine2 ? `${addressLine1}, ${addressLine2}` : addressLine1;

      body.organization.addresses_attributes = [
        {
          country,
          city,
          address,
          province,
          postal_code: postalCode,
          apartment_number: unitNumber,
          building_type: "is_business",
          elevator_access: false,
          is_primary: true,
        },
      ];
    }

    const hasImage = imageFile || uploadLogoLink;

    if (hasImage && isLogoImageUpdated) {
      body.organization.logo_attributes = {
        file: imageFile || uploadLogoLink,
      };
    }

    const formData = hasImage ? objectToFormData(body) : body;

    if (!!organizationID) {
      await updateOrganizationFulfilledFrom(organizationID);
      await organizationDetails.updateOrganization(organizationID, formData);
      history.push(`/organizations/${organizationID}`);
    } else {
      const org = await organizationDetails.createOrganization(formData);
      await updateOrganizationFulfilledFrom(org?.id);
      organizationDetails.reset();
      history.push("/organizations");
    }
  };

  const renderSubmitButton = () => {
    return (
      <div className="btn-wrapper">
        <Button
          icon={organizationID ? <SaveOutlined /> : null}
          onClick={onFormSubmit}
          loading={organizationDetails.loading}
        >
          {organizationID ? "Save" : "Create Organization"}
        </Button>
      </div>
    );
  };

  const renderHeader = () => (
    <div className="header">
      <FulfillmentBackButton onClick={history.goBack} />
    </div>
  );

  const renderAddressForm = () => (
    <AddressForm
      addressLine1={addressLine1}
      unitNumber={unitNumber ?? ""}
      addressLine2={addressLine2}
      city={city}
      province={province}
      postalCode={postalCode}
      country={country}
      setAddressLine1={setAddressLine1}
      setUnitNumber={setUnitNumber}
      setAddressLine2={setAddressLine2}
      setCity={setCity}
      setProvince={setProvince}
      setPostalCode={setPostalCode}
      setCountry={setCountry}
    />
  );

  const renderOrganizationDetailsForm = () => (
    <OrganizationDetailsForm
      fields={[
        {
          label: "Name",
          value: name,
          handler: setName,
        },
        {
          label: "Phone",
          value: phone,
          handler: setPhone,
        },
        {
          label: "Email",
          value: email,
          handler: setEmail,
        },
        {
          label: "Business Name",
          value: businessName,
          handler: setBusinessName,
        },
        {
          label: "Employer Identification Number",
          value: eim,
          handler: setEim,
        },
        {
          label: "Packing notes",
          value: packingNotes,
          handler: setPackingNotes,
          inputAsTextArea: true,
        },
      ]}
    />
  );

  const renderPermissionsAssignment = () => {
    const permissions = get(permissionsIndex, "data", []);
    const filteredPermissions = isGoBolt ? permissions : Permissions.getFilteredPermissions(permissions);

    return (
      <AssignPermissionsForm
        setSelectedPermissions={setSelectedPermissions}
        selectedPermissions={selectedPermissions}
        availablePermissions={filteredPermissions}
      />
    );
  };

  const renderOrganizationCreateEditPage = () => {
    return (
      <Spin spinning={permissionsIndex.loading || organizationDetails.loading}>
        <h2 className="organization-create-edit-page-header">
          {isEmpty(organizationID) ? "Create New " : "Edit "} Organization
        </h2>
        <div className="organization-create-edit-page-wrapper">
          <div className="row">
            <UploadLogo
              uploadLogoLink={uploadLogoLink}
              setUploadLogoLink={setUploadLogoLink}
              setLogoImageUpdated={setIsLogoImageUpdated}
              imageFile={imageFile}
              setImageFile={setImageFile}
              headerText="UPLOAD LOGO"
              allowURLUpload
            />
            {renderOrganizationDetailsForm()}
          </div>
          <div className="row">
            {renderAddressForm()}
            {renderPermissionsAssignment()}
          </div>
          <Row gutter={[0, 24]}>
            <Col span={24}>
              <ShipmentMethodSection
                enabledShippingMethod={enabledShippingMethod}
                onEnabledShippingMethodChange={setEnabledShippingMethod}
                selectedSCServiceTier={selectedSCServiceTier}
                onSelectedSCServiceTierChange={setSelectedSCServiceTier}
              />
            </Col>
            <Col span={24}>
              <ShippingPreferencesSection
                whitelisted={whitelistCarriers}
                blacklisted={blacklistCarriers}
                onSelectWhitelist={setWhitelistCarriers}
                onSelectBlacklist={setBlacklistCarriers}
              />
            </Col>
            <Col span={24}>
              <ParentOrganizationSection
                selectedParentOrg={selectedParentOrg}
                onParentOrganizationChange={handleParentOrganizationChange}
              />
            </Col>
            {!!organizationID && (
              <Col span={24}>
                <GeneralFulfillmentConfig configs={fulfillmentConfigs} setConfigs={setFulfillmentConfigs} />
              </Col>
            )}
            {!!organizationID && (
              <Col span={24}>
                <HubbleMerchantSection
                  organizationID={organizationID}
                  initialHubbleOrganizationID={
                    organizationDetails.data?.configurations_attributes.find(
                      (config) => (config.key as string) === "hubble_organization_id"
                    )?.value
                  }
                  fbbFulfillmentOrgID={
                    organizationDetails.data?.configurations_attributes.find(
                      (config) => (config.key as string) === "fbb_fulfillment_org_id"
                    )?.value
                  }
                />
              </Col>
            )}
            <Col span={24}>
              <GeofilterForm {...{ geofilterConstraints, setGeofilterConstraints }} />
            </Col>
          </Row>
          {renderSubmitButton()}
          {/* TODO - ANNIE: revert non-vessi merchants returns */}
          {/* <div className="row">{renderReturnPreferences()}</div> */}
        </div>
      </Spin>
    );
  };

  return (
    <div id="organization-create-edit-page" className="page-container no-sub-nav-bar">
      <Helmet title={`Sierra - ${isEmpty(organizationID) ? "Create" : "Edit "} Organization`} />
      {renderHeader()}
      {renderOrganizationCreateEditPage()}
    </div>
  );
};

export default OrganizationCreateEditPage;
